import { useEffect, useRef, useState } from 'react'
import { Disc, Download, Trash2, Clock } from 'lucide-react'
import { useRecordingStore, type CallRecording } from '@os/spaces/web/stores/recordingStore'
import { recordingApi } from '../../services/api'
import { dialog } from '../../services/dialog'
import { buildDownloadName } from '../../utils/recordingFilename'

interface Props {
  channelId: string
}

const EMPTY_LIST: ReadonlyArray<any> = []

function fmtDuration(sec?: number | null) {
  if (!sec) return '—'
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function fmtSize(bytes?: number | null) {
  if (!bytes) return ''
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

/**
 * Display label for a recording row in the dropdown — keeps the room
 * name front and centre per the OSPROD spec ("В списке записей
 * желательно отображать название комнаты вместе с датой и временем").
 * Falls back to just the timestamp when the row has no channel context.
 */
function buildDisplayName(rec: CallRecording): string {
  const parts: string[] = []
  if (rec.channel?.server?.name) parts.push(rec.channel.server.name)
  else if (rec.channel?.space?.name) parts.push(rec.channel.space.name)
  if (rec.channel?.name) parts.push(rec.channel.name)
  return parts.length > 0 ? parts.join(' · ') : 'Recording'
}

/** Compact dropdown listing all recordings for a channel. Host-only mutate
 * (delete); everyone can download. */
export default function RecordingsList({ channelId }: Props) {
  const list = useRecordingStore((s) => s.lists[channelId]) ?? EMPTY_LIST
  const isHost = useRecordingStore((s) => !!s.isHost[channelId])
  const loadList = useRecordingStore((s) => s.loadList)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) loadList(channelId)
  }, [open, channelId, loadList])

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const onDelete = async (id: string) => {
    if (!(await dialog.confirm({ message: 'Delete this recording?', tone: 'destructive', confirmLabel: 'Delete' }))) return
    try {
      await recordingApi.remove(id)
      loadList(channelId)
    } catch {}
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        title="Recordings"
        className="w-10 h-10 rounded-full bg-muted text-foreground hover:bg-muted/80 flex items-center justify-center transition duration-150"
      >
        <Clock size={20} />
      </button>
      {open && (
        <div className="absolute bottom-full mb-2 right-0 w-80 max-h-96 overflow-y-auto bg-card rounded-lg ring-1 ring-border shadow-lg z-30">
          <div className="px-3 py-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider border-b border-border">
            Recordings
          </div>
          {list.length === 0 && (
            <div className="px-3 py-4 text-sm text-muted-foreground text-center">No recordings yet.</div>
          )}
          {list.map((r) => {
            const ready = r.status === 'COMPLETED' && !!r.fileUrl
            return (
              <div key={r.id} className="px-3 py-2 border-b border-border last:border-b-0 flex items-center gap-2">
                <Disc
                  size={16}
                  className={
                    r.status === 'ACTIVE' ? 'text-red-500 animate-pulse'
                    : r.status === 'FAILED' ? 'text-destructive'
                    : 'text-muted-foreground'
                  }
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate" title={buildDisplayName(r)}>
                    {buildDisplayName(r)}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {new Date(r.startedAt).toLocaleString()}
                    {' · '}
                    {r.status === 'ACTIVE' || r.status === 'STARTING' ? 'Recording…' : r.status.toLowerCase()}
                    {' · '}{fmtDuration(r.duration)}
                    {fmtSize(r.fileSize) && ' · ' + fmtSize(r.fileSize)}
                  </div>
                </div>
                {ready && r.fileUrl && (
                  <a
                    href={recordingApi.streamUrl(r.id)}
                    download={buildDownloadName(r)}
                    className="w-7 h-7 rounded-md bg-muted text-foreground hover:bg-primary hover:text-primary-foreground flex items-center justify-center"
                    title="Download"
                  >
                    <Download size={14} />
                  </a>
                )}
                {isHost && (
                  <button
                    onClick={() => onDelete(r.id)}
                    className="w-7 h-7 rounded-md bg-muted text-foreground hover:bg-destructive hover:text-destructive-foreground flex items-center justify-center"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
